import { all, call, put, takeLatest } from "redux-saga/effects";
import { request } from "@app/services";
import {
  addTestimonial,
  addTestimonialFailed,
  addTestimonialSuccess,
  deleteTestimonial,
  deleteTestimonialFailed,
  deleteTestimonialSuccess,
  getTestimonialDetail,
  getTestimonialDetailFailed,
  getTestimonialDetailSuccess,
  getTestimonials,
  getTestimonialsFailed,
  getTestimonialsSuccess,
  updateTestimonial,
  updateTestimonialFailed,
  updateTestimonialSuccess,
} from "./slice";
import * as endpoints from "./endpoints";

// Helper function to build body and headers
const buildPayloadData = (payload: any) => {
  if (payload instanceof FormData) {
    return {
      body: payload,
      headers: { "Content-Type": "multipart/form-data" },
    };
  }

  const hasFile = payload?.image && payload.image instanceof File;

  if (hasFile) {
    const formData = new FormData();
    formData.append("reviewerName", payload.reviewerName || "");
    formData.append("rating", String(payload.rating ?? 5));
    formData.append("comment", payload.comment || "");
    formData.append("verifiedBuyer", String(Boolean(payload.verifiedBuyer)));

    if (payload.tag) {
      formData.append("tag", payload.tag);
    }
    formData.append("image", payload.image);

    return {
      body: formData,
      headers: { "Content-Type": "multipart/form-data" },
    };
  }

  const jsonBody: any = {
    reviewerName: payload.reviewerName || "",
    rating: Number(payload.rating ?? 5),
    comment: payload.comment || "",
    verifiedBuyer: Boolean(payload.verifiedBuyer),
  };

  if (payload.tag !== undefined && payload.tag !== null && payload.tag !== "") {
    jsonBody.tag = payload.tag;
  }
  if (payload.image && typeof payload.image === "string") {
    jsonBody.image = payload.image;
  }

  return {
    body: jsonBody,
    headers: {},
  };
};

// ------------------- Get All Testimonials / Reviews -------------------
function* getTestimonialsSaga(action?: any): any {
  try {
    const params = action?.payload || {};
    const response = yield call(request.get, endpoints.testimonials, { params });
    const listData =
      response?.data !== undefined ? response.data : response;
    yield put(getTestimonialsSuccess(listData));
  } catch (error: any) {
    yield put(getTestimonialsFailed(error));
  }
}

// ------------------- Get Single Testimonial Detail -------------------
function* getTestimonialDetailSaga(
  action: ReturnType<typeof getTestimonialDetail>
): any {
  try {
    const { id } = action.payload;
    const response = yield call(
      request.get,
      `${endpoints.testimonialDetails}${id}`
    );
    const detailData =
      response?.data !== undefined ? response.data : response;
    yield put(getTestimonialDetailSuccess(detailData));
  } catch (error: any) {
    yield put(getTestimonialDetailFailed(error));
  }
}

// ------------------- Add Testimonial / Review -------------------
function* addTestimonialSaga(action: ReturnType<typeof addTestimonial>): any {
  try {
    const { body, headers } = buildPayloadData(action.payload);

    const response = yield call(request.post, endpoints.testimonials, body, {
      headers,
    });

    yield put(addTestimonialSuccess(response?.data || response));
    yield put(getTestimonials());
  } catch (error: any) {
    yield put(addTestimonialFailed(error));
  }
}

// ------------------- Update Testimonial / Review -------------------
function* updateTestimonialSaga(
  action: ReturnType<typeof updateTestimonial>
): any {
  try {
    const { id, data } = action.payload;
    const { body, headers } = buildPayloadData(data);

    const response = yield call(
      request.patch,
      `${endpoints.updateTestimonial}${id}`,
      body,
      { headers }
    );

    yield put(updateTestimonialSuccess(response?.data || response));
    yield put(getTestimonials());
  } catch (error: any) {
    yield put(updateTestimonialFailed(error));
  }
}

// ------------------- Delete Testimonial / Review -------------------
function* deleteTestimonialSaga(
  action: ReturnType<typeof deleteTestimonial>
): any {
  try {
    const { id } = action.payload;
    const response = yield call(
      request.delete,
      `${endpoints.deleteTestimonial}${id}`
    );
    yield put(deleteTestimonialSuccess(response?.data || response));
    yield put(getTestimonials());
  } catch (error: any) {
    yield put(deleteTestimonialFailed(error));
  }
}

function* testimonialsSagas() {
  yield all([
    takeLatest(getTestimonials, getTestimonialsSaga),
    takeLatest(getTestimonialDetail, getTestimonialDetailSaga),
    takeLatest(addTestimonial, addTestimonialSaga),
    takeLatest(updateTestimonial, updateTestimonialSaga),
    takeLatest(deleteTestimonial, deleteTestimonialSaga),
  ]);
}

export { testimonialsSagas };
